import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Select, SelectItem } from '../Select';
import { DateTimeField } from '../DateTimeField.jsx';
import {
  Link as LinkIcon,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  ListTodo,
} from 'lucide-react';
import {
  createTask,
  updateTask,
  addDependency,
  removeDependency,
  getTasks,
  getTask,
} from '../../api/task.service';
import { getProject, getProjects } from '../../api/project.service';
import { getModule } from '../../api/module.service';
import { getUserDirectory } from '../../api/user.service';
import useAuthStore from '../../store/useAuthStore';
import useUIStore from '../../store/useUIStore';
import { TASK_CATEGORIES } from '../../constants/taskCategories';
import { getApiErrorMessage } from '../../lib/apiErrors';
import { formatDateInputValue, toIsoFromDateInput } from '../../lib/dateInputs';

const fetchUsers = async () => {
  const { data } = await getUserDirectory();
  return data.data;
};

const fetchProjects = async () => {
  const { data } = await getProjects();
  return data.data;
};

const fetchProject = async (id) => {
  if (!id) return null;
  const { data } = await getProject(id);
  return data.data;
};

const fetchModuleTasks = async (moduleId) => {
  if (!moduleId) return [];
  const { data } = await getTasks({ moduleId });
  return data.data;
};

const fetchTask = async (id) => {
  if (!id) return null;
  const { data } = await getTask(id);
  return data.data;
};

const createInitialFormData = (overrides = {}) => ({
  title: '',
  description: '',
  remark: '',
  category: '',
  assigneeId: '',
  reviewerId: '',
  priority: 'MEDIUM',
  status: 'TODO',
  deadlineAt: '',
  tags: [],
  projectId: '',
  moduleId: '',
  subTasks: [],
  ...overrides,
});

const getDependencyStatusClasses = (status) => {
  switch (status) {
    case 'DONE':
      return 'border-green-200 bg-green-50 text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-300';
    case 'IN_PROGRESS':
      return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300';
    case 'REVIEW':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300';
    default:
      return 'border-border bg-muted/70 text-muted-foreground';
  }
};

const formatDependencyStatus = (status) => (status || 'TODO').replace('_', ' ');

export default function TaskFormModal({
  isOpen,
  onClose,
  editingTask = null,
  defaultProjectId = '',
  defaultModuleId = '',
}) {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const { addToast } = useUIStore();

  const [formData, setFormData] = useState(() =>
    createInitialFormData({
      projectId: defaultProjectId,
      moduleId: defaultModuleId,
    })
  );
  const [pendingBlockerId, setPendingBlockerId] = useState('');
  const [formSubTaskTitle, setFormSubTaskTitle] = useState('');
  const [formSubTaskDeadline, setFormSubTaskDeadline] = useState('');

  const isManagementMode = !!defaultModuleId;

  const handleFormAddSubTask = (e) => {
    e.preventDefault();
    if (!formSubTaskTitle.trim()) return;

    const newSubTask = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 11),
      title: formSubTaskTitle.trim(),
      isCompleted: false,
      deadlineAt: formSubTaskDeadline ? toIsoFromDateInput(formSubTaskDeadline) : null,
    };

    setFormData((prev) => ({
      ...prev,
      subTasks: [...(prev.subTasks || []), newSubTask],
    }));
    setFormSubTaskTitle('');
    setFormSubTaskDeadline('');
  };

  const handleFormToggleSubTask = (index) => {
    setFormData((prev) => {
      const copy = [...(prev.subTasks || [])];
      copy[index] = { ...copy[index], isCompleted: !copy[index].isCompleted };
      return { ...prev, subTasks: copy };
    });
  };

  const handleFormDeleteSubTask = (index) => {
    setFormData((prev) => ({
      ...prev,
      subTasks: (prev.subTasks || []).filter((_, i) => i !== index),
    }));
  };

  const handleFormMoveSubTask = (index, direction) => {
    setFormData((prev) => {
      const copy = [...(prev.subTasks || [])];
      if (direction === 'up' && index > 0) {
        const temp = copy[index];
        copy[index] = copy[index - 1];
        copy[index - 1] = temp;
      } else if (direction === 'down' && index < copy.length - 1) {
        const temp = copy[index];
        copy[index] = copy[index + 1];
        copy[index + 1] = temp;
      }
      return { ...prev, subTasks: copy };
    });
  };

  // Reset form when modal opens or editingTask changes
  useEffect(() => {
    if (isOpen) {
      if (editingTask) {
        setFormData(
          createInitialFormData({
            title: editingTask.title,
            description: editingTask.description,
            remark: editingTask.remark || '',
            category: editingTask.category || '',
            assigneeId: editingTask.assigneeId || '',
            reviewerId: editingTask.reviewerId || '',
            priority: editingTask.priority || 'MEDIUM',
            status: editingTask.status || 'TODO',
            deadlineAt: formatDateInputValue(editingTask.deadlineAt),
            tags: editingTask.tags || [],
            projectId: editingTask.module?.projectId || defaultProjectId,
            moduleId: editingTask.module?.id || defaultModuleId,
            subTasks: editingTask.subTasks || [],
          })
        );
      } else {
        setFormData(
          createInitialFormData({
            projectId: defaultProjectId,
            moduleId: defaultModuleId,
            reviewerId: '',
            subTasks: [],
          })
        );
      }
    }
  }, [isOpen, editingTask, defaultProjectId, defaultModuleId]);

  useEffect(() => {
    if (!isOpen) {
      setPendingBlockerId('');
    }
  }, [isOpen]);

  const { data: users } = useQuery({
    queryKey: ['user-directory'],
    queryFn: fetchUsers,
    enabled: Boolean(isOpen),
  });

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    enabled: Boolean(currentUser && isOpen && !isManagementMode),
  });

  const { data: selectedProject } = useQuery({
    queryKey: ['project', formData.projectId],
    queryFn: () => fetchProject(formData.projectId),
    enabled: Boolean(currentUser && isOpen && formData.projectId),
  });

  const { data: allModuleTasks } = useQuery({
    queryKey: ['all-tasks', formData.moduleId],
    queryFn: () => fetchModuleTasks(formData.moduleId),
    enabled: Boolean(formData.moduleId && isOpen && editingTask),
  });

  const { data: editingTaskDetail } = useQuery({
    queryKey: ['task-detail', editingTask?.id],
    queryFn: () => fetchTask(editingTask.id),
    enabled: Boolean(isOpen && editingTask?.id),
  });

  const activeEditingTask = editingTaskDetail || editingTask;

  const blockerTaskMap = useMemo(
    () => new Map((allModuleTasks || []).map((task) => [task.id, task])),
    [allModuleTasks]
  );

  const existingBlockerIds = useMemo(
    () =>
      new Set(activeEditingTask?.blockedBy?.map((dependency) => dependency.blockingTaskId) || []),
    [activeEditingTask?.blockedBy]
  );

  const availableBlockingTasks = useMemo(() => {
    return (allModuleTasks || [])
      .filter(
        (task) =>
          task.id !== activeEditingTask?.id &&
          task.status !== 'DONE' &&
          !existingBlockerIds.has(task.id)
      )
      .sort((left, right) => left.title.localeCompare(right.title));
  }, [activeEditingTask?.id, allModuleTasks, existingBlockerIds]);

  const isAdmin = currentUser?.globalRole === 'ADMIN';
  const availableProjects = (projects || []).filter((project) => {
    if (isAdmin) return true;
    return project.assignments?.some((assignment) => assignment.userId === currentUser?.id);
  });

  // If we have a selectedProject but it's not in availableProjects (due to loading or filter), add it
  if (selectedProject && !availableProjects.some((p) => p.id === selectedProject.id)) {
    availableProjects.push(selectedProject);
  }

  const availableModules = selectedProject?.modules || [];

  const isProjectManager = Boolean(
    currentUser &&
    (selectedProject?.assignments?.some((a) => a.userId === currentUser.id && a.isManager) ||
      projects
        ?.find((p) => p.id === formData.projectId)
        ?.assignments?.some((a) => a.userId === currentUser.id && a.isManager))
  );
  const isProjectAdmin = isAdmin || isProjectManager;

  const isCreator = editingTask?.creatorId === currentUser?.id;
  const isModuleHead = editingTask?.module?.ownerId === currentUser?.id;
  const isAssignee = editingTask?.assigneeId === currentUser?.id;
  const isReviewer = editingTask?.reviewerId === currentUser?.id;
  const canEditFull = !editingTask || isProjectAdmin || isCreator || isModuleHead;

  const canEditStatus = (() => {
    if (!editingTask) return true;
    if (isProjectAdmin || isModuleHead) return true;
    if (editingTask.status === 'REVIEW') {
      return isReviewer;
    }
    return isCreator || isAssignee || isReviewer;
  })();

  const createMutation = useMutation({
    mutationFn: (newTask) => createTask(newTask),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['project-detail']);
      addToast({ type: 'success', message: 'Task created successfully.' });
      onClose();
    },
    onError: (error) => {
      addToast({ type: 'error', message: getApiErrorMessage(error, 'Unable to create task.') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      if (editingTask?.id) queryClient.invalidateQueries(['task-detail', editingTask.id]);
      addToast({ type: 'success', message: 'Task updated successfully.' });
      onClose();
    },
    onError: (error) => {
      addToast({ type: 'error', message: getApiErrorMessage(error, 'Unable to update task.') });
    },
  });

  const addDependencyMutation = useMutation({
    mutationFn: (blockingTaskId) => addDependency(editingTask.id, { blockingTaskId }),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      if (editingTask?.id) queryClient.invalidateQueries(['task-detail', editingTask.id]);
      setPendingBlockerId('');
      addToast({ type: 'success', message: 'Blocker added successfully.' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: getApiErrorMessage(error, 'Unable to add blocker.') });
    },
  });

  const removeDependencyMutation = useMutation({
    mutationFn: (blockingTaskId) => removeDependency(editingTask.id, blockingTaskId),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      if (editingTask?.id) queryClient.invalidateQueries(['task-detail', editingTask.id]);
      addToast({ type: 'success', message: 'Blocker removed successfully.' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: getApiErrorMessage(error, 'Unable to remove blocker.') });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.moduleId) {
      addToast({ type: 'error', message: 'Please complete all required fields.' });
      return;
    }

    if (formData.status === 'REVIEW' && !formData.reviewerId) {
      addToast({
        type: 'error',
        message: 'A reviewer must be assigned when moving the task to REVIEW status.',
      });
      return;
    }

    const taskPayload = {
      ...formData,
      deadlineAt: toIsoFromDateInput(formData.deadlineAt),
    };

    if (editingTask) {
      updateMutation.mutate({ id: editingTask.id, ...taskPayload });
    } else {
      createMutation.mutate(taskPayload);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 border border-border">
        <div className="px-card py-4 border-b border-border flex items-center justify-between bg-muted/50">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {editingTask ? 'Edit Task' : 'New Task'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl font-light"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-card space-y-gap max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                type="text"
                required
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {!editingTask && !isManagementMode && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Project *
                  </label>
                  <Select
                    required
                    className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none"
                    value={formData.projectId}
                    onChange={(e) =>
                      setFormData((current) => ({
                        ...current,
                        projectId: e.target.value,
                        moduleId: '',
                      }))
                    }
                  >
                    <SelectItem value="">Select project</SelectItem>
                    {availableProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Module *</label>
                  <Select
                    required
                    disabled={!formData.projectId || availableModules.length === 0}
                    className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                    value={formData.moduleId}
                    onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                  >
                    <SelectItem value="">Select module</SelectItem>
                    {availableModules.map((module) => (
                      <SelectItem key={module.id} value={module.id}>
                        {module.name}
                      </SelectItem>
                    ))}
                  </Select>
                  {!formData.projectId ? (
                    <p className="mt-1 text-[10px] italic text-muted-foreground">
                      Choose a project first.
                    </p>
                  ) : null}
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Description</label>
              <textarea
                rows={2}
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50 resize-none"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Category</label>
              <Select
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <SelectItem value="">No Category</SelectItem>
                {TASK_CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Assignee</label>
              <Select
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.assigneeId}
                onChange={(e) => setFormData({ ...formData, assigneeId: e.target.value })}
              >
                <SelectItem value="">Unassigned</SelectItem>
                {users?.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </Select>
              {!canEditFull && (
                <p className="mt-1 text-[10px] text-muted-foreground italic">
                  Only creators or admins can reassign.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Reviewer</label>
              <Select
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.reviewerId}
                onChange={(e) => setFormData({ ...formData, reviewerId: e.target.value })}
              >
                <SelectItem value="">Select a reviewer...</SelectItem>
                {users?.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <Select
                disabled={!canEditStatus}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <SelectItem value="BACKLOG">Backlog</SelectItem>
                <SelectItem value="TODO">To Do</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="REVIEW">Review</SelectItem>
                <SelectItem value="DONE">Done</SelectItem>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
              <Select
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Deadline</label>
              <DateTimeField
                type="date"
                disabled={!canEditFull}
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none disabled:opacity-50"
                value={formData.deadlineAt}
                onChange={(e) => setFormData({ ...formData, deadlineAt: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">
                Remarks / Notes
              </label>
              <textarea
                rows={2}
                placeholder="Add progress notes or remarks..."
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none resize-none"
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
              />
            </div>
          </div>

          {editingTask && isManagementMode && canEditFull && (
            <div className="pt-6 mt-2 border-t border-border">
              <label className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                <LinkIcon className="w-4 h-4 text-primary" />
                Blockers
              </label>
              <p className="mb-3 text-xs leading-5 text-muted-foreground">
                Add the tasks that must finish before this one can move forward. Remove a blocker
                only when this task is no longer waiting on it.
              </p>

              <div className="mb-4 space-y-2">
                {activeEditingTask?.blockedBy?.length ? (
                  activeEditingTask.blockedBy.map((dependency) => {
                    const blockerTask =
                      dependency.blockingTask || blockerTaskMap.get(dependency.blockingTaskId);

                    return (
                      <div
                        key={dependency.id}
                        className="rounded-2xl border border-red-200 bg-red-50/70 p-3 dark:border-red-500/20 dark:bg-red-500/10"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                              This task is waiting on
                            </p>
                            <p className="mt-1 text-sm font-semibold text-foreground">
                              {blockerTask?.title || 'Blocking task'}
                            </p>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                              <span
                                className={`rounded-full border px-2 py-1 font-medium ${getDependencyStatusClasses(blockerTask?.status)}`}
                              >
                                {formatDependencyStatus(blockerTask?.status)}
                              </span>
                              <span className="text-muted-foreground">
                                {blockerTask?.assignee?.name || 'Unassigned'}
                              </span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeDependencyMutation.mutate(dependency.blockingTaskId)
                            }
                            disabled={removeDependencyMutation.isPending}
                            className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/20 dark:text-red-300 dark:hover:bg-red-500/10"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
                    No blockers attached. This task can move independently.
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-border bg-muted/20 p-3">
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Add blocker
                    </label>
                    <Select
                      className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-xl text-sm outline-none focus:ring-2 focus:ring-ring"
                      onChange={(e) => setPendingBlockerId(e.target.value)}
                      value={pendingBlockerId}
                      placeholder="Choose a task that must finish first"
                    >
                      {availableBlockingTasks.map((task) => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.title}
                        </SelectItem>
                      ))}
                    </Select>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Only incomplete tasks from the same module are available as blockers.
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={!pendingBlockerId || addDependencyMutation.isPending}
                    onClick={() => addDependencyMutation.mutate(pendingBlockerId)}
                    className="h-10 rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add blocker
                  </button>
                </div>

                {availableBlockingTasks.length === 0 && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    No eligible blocker tasks found.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Sub-tasks Section in Form */}
          <div className="pt-6 mt-2 border-t border-border">
            <label className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
              <ListTodo className="w-4 h-4 text-primary" />
              Sub-tasks
            </label>
            <p className="mb-3 text-xs leading-5 text-muted-foreground">
              Define the sub-tasks required to complete this task. You can add, reorder, delete, or mark them completed here.
            </p>

            <div className="mb-4 space-y-2">
              {formData.subTasks && formData.subTasks.length > 0 ? (
                formData.subTasks.map((st, index) => (
                  <div
                    key={st.id || index}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/20 px-3 py-2 transition-colors hover:bg-muted/40"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <input
                        type="checkbox"
                        checked={st.isCompleted || false}
                        onChange={() => handleFormToggleSubTask(index)}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                      />
                      <span
                        className={`truncate text-sm text-foreground ${
                          st.isCompleted ? 'text-muted-foreground line-through decoration-1' : ''
                        }`}
                      >
                        {st.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="w-36 sm:w-44">
                        <DateTimeField
                          type="date"
                          placeholder="No deadline"
                          value={st.deadlineAt ? formatDateInputValue(st.deadlineAt) : ''}
                          onChange={(e) => {
                            const newDate = e.target.value ? toIsoFromDateInput(e.target.value) : null;
                            setFormData((prev) => {
                              const copy = [...(prev.subTasks || [])];
                              copy[index] = { ...copy[index], deadlineAt: newDate };
                              return { ...prev, subTasks: copy };
                            });
                          }}
                          futureOnly={false}
                        />
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => handleFormMoveSubTask(index, 'up')}
                          className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={index === formData.subTasks.length - 1}
                          onClick={() => handleFormMoveSubTask(index, 'down')}
                          className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleFormDeleteSubTask(index)}
                          className="rounded-lg p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
                  No sub-tasks added yet.
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-muted/20 p-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  placeholder="Enter sub-task title..."
                  value={formSubTaskTitle}
                  onChange={(e) => setFormSubTaskTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFormAddSubTask(e);
                    }
                  }}
                  className="flex-1 px-3 py-2 bg-background text-foreground border border-input rounded-xl text-sm outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="flex gap-2 shrink-0">
                  <div className="w-40">
                    <DateTimeField
                      type="date"
                      placeholder="Deadline (optional)"
                      value={formSubTaskDeadline}
                      onChange={(e) => setFormSubTaskDeadline(e.target.value)}
                      futureOnly={false}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!formSubTaskTitle.trim()}
                    onClick={handleFormAddSubTask}
                    className="h-9 rounded-xl bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-input text-foreground rounded-xl font-medium hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              )}
              {editingTask ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
